import { useEffect, useState, useMemo } from "react";
import { useWeb } from "../context/Dashboard/contextWeb";
import { useNetwork } from "../context/Dashboard/contextNetwork";
import { useAwsConfig } from "../context/Dashboard/aws/contextConfig";
import { useAwsUrl } from "../context/Dashboard/aws/contextUrl";
import { useAwsIp } from "../context/Dashboard/aws/contextIp";
import { useAzureConfig } from "../context/Dashboard/azure/contextConfig";
import { useAzureUrl } from "../context/Dashboard/azure/contextUrl";
import { useAzureIp } from "../context/Dashboard/azure/contextIp";
import { useGcpConfig } from "../context/Dashboard/gcp/contextConfig";
import { useGcpUrl } from "../context/Dashboard/gcp/contextUrl";
import { useGcpIp } from "../context/Dashboard/gcp/contextIp";
import { useOciConfig } from "../context/Dashboard/oci/contextConfig";

const useNav = () => {
  // 🔹 Sidebar Active States
  const [activeNav, setActiveNav] = useState(0);
  const [activeCloudSubNav, setActiveCloudSubNav] = useState(null);
  const [openCloudSubMenu, setOpenCloudSubMenu] = useState(false);
  const [activeCloudTab, setActiveCloudTab] = useState(0);

  const [activeComplianceSubNav, setActiveComplianceSubNav] = useState(null);
  const [openComplianceSubMenu, setOpenComplianceSubMenu] = useState(false);
  const [activeComplianceTab, setActiveComplianceTab] = useState(0);

  const [reportTab, setReportTab] = useState("Web Reports");
  const [reportSubTab, setReportSubTab] = useState("");

  const { webResults } = useWeb();
  const { networkResults } = useNetwork();

  // 🔹 CLOUD CONTEXTS
  const { configAssets: awsConfigAssets } = useAwsConfig();
  const { webResults: awsUrlAssets } = useAwsUrl();
  const { networkResults: awsIpAssets } = useAwsIp();

  const { configAssets: azureConfigAssets } = useAzureConfig();
  const { webResults: azureUrlAssets } = useAzureUrl();
  const { networkResults: azureIpAssets } = useAzureIp();

  const { configAssets: gcpConfigAssets } = useGcpConfig();
  const { webResults: gcpUrlAssets } = useGcpUrl();
  const { networkResults: gcpIpAssets } = useGcpIp();
  const { configAssets: ociConfigAssets } = useOciConfig();

  const hasWebAssets = useMemo(() => {
    const web = webResults?.length;
    return web;
  }, [webResults]);

  const hasNetworkAssets = useMemo(() => {
    const network = networkResults?.length;
    return network;
  }, [networkResults]);

  // 🔹 CLOUD: Check if any assets exist
  const hasCloudAssets = useMemo(() => {
    const aws = awsConfigAssets?.length || awsUrlAssets?.length || awsIpAssets?.length;
    const azure = azureConfigAssets?.length || azureUrlAssets?.length || azureIpAssets?.length;
    const gcp = gcpConfigAssets?.length || gcpUrlAssets?.length || gcpIpAssets?.length;
    const oci = ociConfigAssets?.length;
    return aws || azure || gcp || oci;
  }, [
    awsConfigAssets, awsUrlAssets, awsIpAssets,
    azureConfigAssets, azureUrlAssets, azureIpAssets,
    gcpConfigAssets, gcpUrlAssets, gcpIpAssets,
    ociConfigAssets
  ]);

  const hasAwsAssets = useMemo(() => {
    const aws = awsConfigAssets?.length || awsUrlAssets?.length || awsIpAssets?.length;
    return aws;
  }, [
    awsConfigAssets, awsUrlAssets, awsIpAssets
  ]);

  const hasAzureAssets = useMemo(() => {
    const azure = azureConfigAssets?.length || azureUrlAssets?.length || azureIpAssets?.length;
    return azure;
  }, [
    azureConfigAssets, azureUrlAssets, azureIpAssets
  ]);

  const hasGcpAssets = useMemo(() => {
    const gcp = gcpConfigAssets?.length || gcpUrlAssets?.length || gcpIpAssets?.length;
    return gcp;
  }, [
    gcpConfigAssets, gcpUrlAssets, gcpIpAssets
  ]);

  const hasOciAssets = useMemo(() => {
    return ociConfigAssets?.length;
  }, [ociConfigAssets]);

  const hasComplianceAssets = useMemo(() => {
    return false;
  }, []);

  useEffect(() => {
    sessionStorage.setItem("activeSidebarIndex", activeNav);
  }, [activeNav]);

  useEffect(() => {
    if (activeNav === 3 && hasCloudAssets) {
      setOpenCloudSubMenu(true);
      setOpenComplianceSubMenu(false);
    } else if (activeNav === 7 && hasComplianceAssets) {
      setOpenComplianceSubMenu(true);
      setOpenCloudSubMenu(false);
    }
  }, [activeNav, hasCloudAssets, hasComplianceAssets]);

  useEffect(() => {
    if (activeNav !== 3) return;

    if (hasAwsAssets) {
      setActiveCloudTab(0);
      return;
    }

    if (hasAzureAssets) {
      setActiveCloudTab(0);
      return;
    }

    if (hasGcpAssets) {
      setActiveCloudTab(0);
      return;
    }

    if (hasOciAssets) {
      setActiveCloudTab(0);
    }
  }, [
    activeNav,
    hasAwsAssets,
    hasAzureAssets,
    hasGcpAssets,
    hasOciAssets
  ]);

  // 🧾 Compliance logic placeholder for later
  useEffect(() => {
    if (activeNav !== 7) return;

    // Example future logic:
    // if (pciAssets?.length) setActiveComplianceSubNav(0);
    // if (isoAssets?.length) setActiveComplianceSubNav(1);
  }, [activeNav]);

  // 🧹 Reset Submenus
  const resetSubNav = (options = { keepCloud: false, keepCompliance: false }) => {
    if (!options.keepCloud) {
      setActiveCloudSubNav(null);
      setOpenCloudSubMenu(false);
    }
    if (!options.keepCompliance) {
      setActiveComplianceSubNav(null);
      setOpenComplianceSubMenu(false);
    }
  };

  const resetState = () => {
    setActiveNav(0);
    setActiveCloudSubNav(null);
    setOpenCloudSubMenu(false);
    setActiveCloudTab(0);
    setActiveComplianceSubNav(null);
    setOpenComplianceSubMenu(false);
    setActiveComplianceTab(0);
    setReportTab("Web Reports");
    setReportSubTab("");
  };

  return {
    resetState,
    resetSubNav,
    activeNav,
    setActiveNav,
    activeCloudSubNav,
    setActiveCloudSubNav,
    activeCloudTab,
    setActiveCloudTab,
    openCloudSubMenu,
    setOpenCloudSubMenu,
    activeComplianceSubNav,
    setActiveComplianceSubNav,
    activeComplianceTab,
    setActiveComplianceTab,
    openComplianceSubMenu,
    setOpenComplianceSubMenu,
    reportTab, setReportTab,
    reportSubTab, setReportSubTab,

    hasWebAssets,
    hasNetworkAssets,
    hasCloudAssets,
    hasComplianceAssets,
    hasAwsAssets,
    hasAzureAssets,
    hasGcpAssets,
    hasOciAssets,
  };
};

export default useNav;
